import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Divider,
  Typography,
} from "@mui/material";
import { FcPlus } from "react-icons/fc";
import { useCreateOrganization } from "../../../graphql/OrganizationAPI/OrganizationMutation";
import { OrganizationType } from "../../../graphql/OrganizationAPI/OrganizationMutation";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useAppThunkDispatch } from "../../../Redux/hooks";
import { AddingNewAdminWithDescription } from "../../../Redux/Actions/AdminActions/AddingNewAdminWithDesc";
import { OwnerType } from "../../../Redux/MasjidTypes";
import AddressInput from "../../Shared/AddressInput/address-input";

interface OrganizationFormProps {
  open: boolean;
  onClose: () => void;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const { createOrganization, loading: createLoading } =
    useCreateOrganization();

  // Organization form refs
  const nameRef = useRef<HTMLInputElement | null>(null);
  const descriptionRef = useRef<HTMLInputElement | null>(null);
  const contactRef = useRef<any>(null);

  // Admin user form refs
  const adminNameRef = useRef<HTMLInputElement | null>(null);
  const adminEmailRef = useRef<HTMLInputElement | null>(null);
  const adminPhoneRef = useRef<any>(null);

  // Form state
  const [organizationType, setOrganizationType] = useState<OrganizationType>(
    OrganizationType.NON_PROFIT
  );
  const [addressData, setAddressData] = useState<{
    address: string;
    lat: number;
    lng: number;
    isValid: boolean;
  }>({
    address: "",
    lat: 0,
    lng: 0,
    isValid: false,
  });

  // Error states for organization
  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [contactError, setContactError] = useState(false);

  // Error states for admin user
  const [adminNameError, setAdminNameError] = useState(false);
  const [adminEmailError, setAdminEmailError] = useState(false);
  const [adminPhoneError, setAdminPhoneError] = useState(false);

  const handleClose = () => {
    // Reset organization form
    setNameError(false);
    setDescriptionError(false);
    setAddressError(false);
    setContactError(false);
    setOrganizationType(OrganizationType.NON_PROFIT);
    setAddressData({
      address: "",
      lat: 0,
      lng: 0,
      isValid: false,
    });

    // Reset admin user form
    setAdminNameError(false);
    setAdminEmailError(false);
    setAdminPhoneError(false);

    // Clear organization form fields
    if (nameRef.current) nameRef.current.value = "";
    if (descriptionRef.current) descriptionRef.current.value = "";
    if (contactRef.current) contactRef.current.value = "";

    // Clear admin user form fields
    if (adminNameRef.current) adminNameRef.current.value = "";
    if (adminEmailRef.current) adminEmailRef.current.value = "";
    if (adminPhoneRef.current) adminPhoneRef.current.value = "";

    onClose();
  };

  const validateForm = () => {
    let isValid = true;

    // Validate organization required fields
    if (!nameRef.current?.value.trim()) {
      setNameError(true);
      isValid = false;
    } else {
      setNameError(false);
    }

    if (!descriptionRef.current?.value.trim()) {
      setDescriptionError(true);
      isValid = false;
    } else {
      setDescriptionError(false);
    }

    if (!addressData.address.trim() || !addressData.isValid) {
      setAddressError(true);
      isValid = false;
    } else {
      setAddressError(false);
    }

    // Validate admin user required fields
    if (!adminNameRef.current?.value.trim()) {
      setAdminNameError(true);
      isValid = false;
    } else {
      setAdminNameError(false);
    }

    if (!adminEmailRef.current?.value.trim()) {
      setAdminEmailError(true);
      isValid = false;
    } else {
      setAdminEmailError(false);
    }

    if (!adminPhoneRef.current?.value) {
      setAdminPhoneError(true);
      isValid = false;
    } else {
      setAdminPhoneError(false);
    }

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // Create organization
      const coordinates = [addressData.lng, addressData.lat];

      const organizationData = {
        name: nameRef.current?.value || "",
        description: descriptionRef.current?.value || "",
        address: addressData.address,
        location: {
          type: "Point",
          coordinates: coordinates as [number, number],
        },
        contact: contactRef.current?.value?.replace(/\s+/g, "") || "",
        organizationType,
      };

      const organizationResponse = await createOrganization(organizationData);

      if (organizationResponse?._id) {
        // Create admin user with organization ID as masjidId
        const adminData = {
          name: adminNameRef.current?.value || "",
          email: adminEmailRef.current?.value.toLowerCase() || "",
          role: "subadmin", // Hardcoded as requested
          contact: adminPhoneRef.current?.value?.replace(/\s+/g, "") || "",
          description: `Admin for organization: ${nameRef.current?.value}`,
          ownerType: OwnerType.ORGANIZATION, // Automatically set for organizations
          masjidId: organizationResponse._id, // Pass organization ID as masjidId
        };

        const adminResponse = await dispatch(
          AddingNewAdminWithDescription(adminData)
        );

        if (adminResponse?._id) {
          toast.success("Organization and admin user created successfully");
          setTimeout(() => {
            navigate("/organizations");
          }, 1000);
          handleClose();
        } else {
          toast.error("Organization created but admin user creation failed");
        }
      }
    } catch (error: any) {
      console.error("Error creating organization:", error);
      toast.error(
        `Error creating organization: ${error.message || "Unknown error"}`
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "22px",
          padding: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          color: "#1B8368",
          fontSize: "20px",
          fontWeight: "600",
          padding: "20px",
        }}
      >
        Add New Organization
      </DialogTitle>
      <DialogContent sx={{ padding: "20px" }}>
        {/* Organization Details Section */}
        <Typography variant="h6" sx={{ mb: 2, color: "#1B8368" }}>
          Organization Details
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 3,
          }}
        >
          <TextField
            inputRef={nameRef}
            label="Organization Name"
            placeholder="Enter the organization name"
            fullWidth
            error={nameError}
            helperText={nameError ? "Organization name is required" : ""}
            sx={{ marginBottom: 2 }}
          />

          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            <InputLabel>Organization Type</InputLabel>
            <Select
              value={organizationType}
              label="Organization Type"
              onChange={(e) =>
                setOrganizationType(e.target.value as OrganizationType)
              }
            >
              <MenuItem value={OrganizationType.NON_PROFIT}>
                Non-Profit
              </MenuItem>
              <MenuItem value={OrganizationType.FOR_PROFIT}>
                For-Profit
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            inputRef={descriptionRef}
            label="Description"
            placeholder="Enter the description of the organization"
            fullWidth
            multiline
            rows={3}
            error={descriptionError}
            helperText={descriptionError ? "Description is required" : ""}
            sx={{ marginBottom: 2, gridColumn: "span 2" }}
          />

          <Box sx={{ gridColumn: "span 2", marginBottom: 2, position: "relative", zIndex: 1000 }}>
            <AddressInput
              value={addressData.address}
              onChange={(data) => {
                setAddressData(data);
                setAddressError(false);
              }}
              label="Address"
              placeholder="Enter or search address"
              error={addressError}
              required={true}
              borderRadius="md"
              height="md"
            />
          </Box>

          <Box sx={{ gridColumn: "span 2", marginBottom: 2 }}>
            <PhoneInput
              ref={contactRef}
              international
              countryCallingCodeEditable={false}
              defaultCountry="US"
              placeholder="Enter contact number"
              onChange={() => {}} // Required prop
              style={{
                width: "100%",
                height: "56px",
                border: contactError ? "2px solid #d32f2f" : "1px solid #ccc",
                borderRadius: "4px",
                padding: "8px 12px",
              }}
            />
            {contactError && (
              <div
                style={{
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  marginTop: "4px",
                }}
              >
                Contact number is required
              </div>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Admin User Details Section */}
        <Typography variant="h6" sx={{ mb: 2, color: "#1B8368" }}>
          Admin User Details
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            inputRef={adminNameRef}
            label="Admin Name"
            placeholder="Enter admin name"
            fullWidth
            error={adminNameError}
            helperText={adminNameError ? "Admin name is required" : ""}
            sx={{ marginBottom: 2 }}
          />

          <TextField
            inputRef={adminEmailRef}
            label="Admin Email"
            placeholder="Enter admin email"
            type="email"
            fullWidth
            error={adminEmailError}
            helperText={adminEmailError ? "Admin email is required" : ""}
            sx={{ marginBottom: 2 }}
          />

          <Box sx={{ gridColumn: "span 2", marginBottom: 2 }}>
            <PhoneInput
              ref={adminPhoneRef}
              international
              countryCallingCodeEditable={false}
              defaultCountry="US"
              placeholder="Enter admin phone number"
              onChange={() => {}} // Required prop
              style={{
                width: "100%",
                height: "56px",
                border: adminPhoneError ? "2px solid #d32f2f" : "1px solid #ccc",
                borderRadius: "4px",
                padding: "8px 12px",
              }}
            />
            {adminPhoneError && (
              <div
                style={{
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  marginTop: "4px",
                }}
              >
                Admin phone number is required
              </div>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "20px" }}>
        <Button
          onClick={handleClose}
          sx={{
            color: "#666",
            border: "1px solid #ccc",
            "&:hover": {
              border: "1px solid #999",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={createLoading}
          sx={{
            backgroundColor: "#1B8368",
            color: "white",
            "&:hover": {
              backgroundColor: "#156B52",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
          startIcon={
            createLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FcPlus />
            )
          }
        >
          {createLoading ? "Creating..." : "Create Organization"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationForm;
